import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users, Activity, Flame, TrendingUp, CheckCircle2, UserCheck,
  Search, ChevronLeft, ChevronRight, X, ArrowUpDown, BarChart3,
  Calendar, Clock, Star, Shield, Eye
} from 'lucide-react';

// ──────── Stats Card ────────
function StatCard({ icon: Icon, label, value, sub, color = 'primary' }) {
  const colors = {
    primary: 'text-[--hc-primary] bg-[--hc-primary]/5',
    accent: 'text-[--hc-accent] bg-[--hc-accent]/10',
    success: 'text-[--hc-success] bg-[--hc-success-soft]',
    muted: 'text-[--hc-text-muted] bg-[--hc-surface]',
  };
  return (
    <div className="rounded-xl border border-[--hc-border] bg-white px-4 py-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon size={16} />
        </div>
        <span className="text-xs font-medium text-[--hc-text-muted] uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-[--hc-text] tabular-nums">{value}</p>
      {sub && <p className="text-xs text-[--hc-text-muted] mt-0.5">{sub}</p>}
    </div>
  );
}

// ──────── Signup Trend Mini Chart ────────
function SignupTrendChart({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="rounded-xl border border-[--hc-border] bg-white p-4">
      <h3 className="text-sm font-semibold text-[--hc-text] mb-3 flex items-center gap-2">
        <BarChart3 size={14} className="text-[--hc-primary]" />
        Signups (Last 14 Days)
      </h3>
      <div className="flex items-end gap-1 h-16">
        {data.map((d, i) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div
              className="w-full rounded-t bg-[--hc-primary] hover:bg-[--hc-accent] transition-colors cursor-pointer min-h-[2px]"
              style={{ height: `${Math.max((d.count / max) * 100, 4)}%` }}
            />
            <div className="hidden group-hover:block absolute -top-8 bg-[--hc-text] text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
              {format(new Date(d.date + 'T12:00:00'), 'MMM d')}: {d.count}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-[--hc-text-muted]">{format(new Date(data[0].date + 'T12:00:00'), 'MMM d')}</span>
        <span className="text-[10px] text-[--hc-text-muted]">{format(new Date(data[data.length - 1].date + 'T12:00:00'), 'MMM d')}</span>
      </div>
    </div>
  );
}

// ──────── User Detail Modal ────────
function UserDetailModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/user/${userId}`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load user details'))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-[600px] w-full max-h-[85vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
        data-testid="admin-user-detail-modal"
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[--hc-border] sticky top-0 bg-white z-10">
          <h3 className="text-sm font-semibold text-[--hc-text]">User Details</h3>
          <button onClick={onClose} className="text-[--hc-text-muted] hover:text-[--hc-text] p-1 rounded-lg hover:bg-[--hc-surface]">
            <X size={18} />
          </button>
        </div>

        {loading && <div className="p-8 text-center text-sm text-[--hc-text-muted]">Loading...</div>}

        {!loading && data && (
          <div className="p-5 space-y-5">
            {/* User info */}
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[--hc-primary] flex items-center justify-center text-white text-sm font-semibold">
                {data.user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="text-sm font-semibold text-[--hc-text]">{data.user.name}</p>
                <p className="text-xs text-[--hc-text-muted]">{data.user.email}</p>
              </div>
              <div className="ml-auto text-right">
                {data.user.is_admin && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[--hc-accent] bg-[--hc-accent]/10 px-2 py-0.5 rounded-full">
                    <Shield size={10} /> Admin
                  </span>
                )}
                {data.user.sso_user && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[--hc-primary] bg-[--hc-primary]/10 px-2 py-0.5 rounded-full ml-1">
                    SSO
                  </span>
                )}
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="rounded-lg bg-[--hc-surface] px-3 py-2 text-center">
                <p className="text-lg font-bold text-[--hc-primary] tabular-nums">{data.stats.current_streak}</p>
                <p className="text-[10px] text-[--hc-text-muted]">Current Streak</p>
              </div>
              <div className="rounded-lg bg-[--hc-surface] px-3 py-2 text-center">
                <p className="text-lg font-bold text-[--hc-primary] tabular-nums">{data.stats.best_streak}</p>
                <p className="text-[10px] text-[--hc-text-muted]">Best Streak</p>
              </div>
              <div className="rounded-lg bg-[--hc-surface] px-3 py-2 text-center">
                <p className="text-lg font-bold text-[--hc-primary] tabular-nums">{data.total_completed_days}</p>
                <p className="text-[10px] text-[--hc-text-muted]">Completed Days</p>
              </div>
              <div className="rounded-lg bg-[--hc-surface] px-3 py-2 text-center">
                <p className="text-lg font-bold text-[--hc-primary] tabular-nums">{data.total_partial_days}</p>
                <p className="text-[10px] text-[--hc-text-muted]">Partial Days</p>
              </div>
            </div>

            {/* Meta info */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div><span className="text-[--hc-text-muted]">Timezone:</span> <span className="text-[--hc-text]">{data.user.timezone}</span></div>
              <div><span className="text-[--hc-text-muted]">Joined:</span> <span className="text-[--hc-text]">{data.user.created_at ? format(new Date(data.user.created_at), 'MMM d, yyyy') : '—'}</span></div>
              <div><span className="text-[--hc-text-muted]">Onboarded:</span> <span className="text-[--hc-text]">{data.user.onboarding_complete ? 'Yes' : 'No'}</span></div>
              <div><span className="text-[--hc-text-muted]">Review Day:</span> <span className="text-[--hc-text]">{data.user.weekly_review_day}</span></div>
            </div>

            {/* Routine */}
            {data.routine && (
              <div>
                <h4 className="text-xs font-semibold text-[--hc-text-muted] uppercase tracking-wide mb-2">
                  Routine (v{data.routine.version}) — {data.routine.wake_time} → {data.routine.start_operating_time}
                </h4>
                <div className="space-y-1">
                  {data.routine.items?.filter(i => i.section === 'before').map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <span className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold ${
                        item.grade === '+' ? 'bg-[--hc-success-soft] text-[--hc-success]' :
                        item.grade === '/' ? 'bg-[--hc-surface-2] text-[--hc-text-muted]' :
                        'bg-[--hc-danger-soft] text-[--hc-danger]'
                      }`}>{item.grade}</span>
                      <span className="text-[--hc-text]">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent activity (last 10 days) */}
            {data.recent_logs?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-[--hc-text-muted] uppercase tracking-wide mb-2">Recent Activity</h4>
                <div className="flex gap-1 flex-wrap">
                  {data.recent_logs.slice(0, 14).map((log, idx) => (
                    <div
                      key={idx}
                      className={`w-7 h-7 rounded flex items-center justify-center text-[9px] font-medium ${
                        log.status === 'complete' ? 'bg-[--hc-accent] text-[--hc-on-accent]' :
                        log.status === 'partial' ? 'bg-[#a7b8d3] text-white' :
                        'bg-[--hc-surface-2] text-[--hc-text-muted]'
                      }`}
                      title={`${log.date}: ${log.status}`}
                    >
                      {format(new Date(log.date + 'T12:00:00'), 'd')}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weekly reviews */}
            {data.reviews?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-[--hc-text-muted] uppercase tracking-wide mb-2">Weekly Reviews</h4>
                <div className="space-y-1.5">
                  {data.reviews.slice(0, 5).map((rev, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <Star size={12} className="text-[--hc-accent]" />
                      <span className="font-medium text-[--hc-text] tabular-nums">{rev.consistency_score}/10</span>
                      <span className="text-[--hc-text-muted]">— Week of {format(new Date(rev.week_start_date + 'T12:00:00'), 'MMM d')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────── Main Admin Page ────────
export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/users', {
        params: { search, sort_by: sortBy, sort_dir: sortDir, page, per_page: 20 }
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch {
      // handled by stats error
    }
  }, [search, sortBy, sortDir, page]);

  useEffect(() => {
    Promise.all([loadStats(), loadUsers()]).finally(() => setLoading(false));
  }, [loadStats, loadUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-[--hc-text-muted] text-sm">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[--hc-text] flex items-center gap-2">
            <Shield size={20} className="text-[--hc-accent]" />
            Admin Dashboard
          </h2>
          <p className="text-sm text-[--hc-text-muted]">Platform engagement and user management</p>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats-grid">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} color="primary" />
            <StatCard icon={Activity} label="Active Today" value={stats.daily_active} sub={`${stats.daily_completed} completed`} color="success" />
            <StatCard icon={TrendingUp} label="WAU (7d)" value={stats.weekly_active} color="primary" />
            <StatCard icon={Calendar} label="MAU (30d)" value={stats.monthly_active} color="primary" />
            <StatCard icon={Flame} label="Avg Streak" value={stats.avg_streak} sub="across active users" color="accent" />
            <StatCard icon={UserCheck} label="Onboarded" value={`${stats.onboarding_rate}%`} sub={`${stats.onboarded_users} of ${stats.total_users}`} color="success" />
          </div>

          {/* Completion rate + trend */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-[--hc-border] bg-white p-4">
              <h3 className="text-sm font-semibold text-[--hc-text] mb-2 flex items-center gap-2">
                <CheckCircle2 size={14} className="text-[--hc-success]" />
                Today's Completion Rate
              </h3>
              <div className="flex items-end gap-3">
                <span className="text-3xl font-bold text-[--hc-primary] tabular-nums">{stats.completion_rate}%</span>
                <span className="text-xs text-[--hc-text-muted] pb-1">
                  {stats.daily_completed} of {stats.daily_active || 'no'} active users completed all items
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-[--hc-surface-2] mt-3 overflow-hidden">
                <div className="h-full rounded-full bg-[--hc-accent] transition-all" style={{ width: `${stats.completion_rate}%` }} />
              </div>
            </div>
            <SignupTrendChart data={stats.signup_trend} />
          </div>
        </>
      )}

      {/* User Table */}
      <div className="rounded-xl border border-[--hc-border] bg-white overflow-hidden" data-testid="admin-users-table">
        <div className="px-4 py-3 border-b border-[--hc-border] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold text-[--hc-text]">All Users ({total})</h3>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[--hc-text-muted]" />
              <input
                data-testid="admin-search-input"
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-8 pr-3 py-2 rounded-lg border border-[--hc-border] text-xs w-[220px] focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
                placeholder="Search by name or email..."
              />
            </div>
            <button
              type="submit"
              className="px-3 py-2 rounded-lg bg-[--hc-primary] text-white text-xs font-medium hover:bg-[--hc-primary-hover] transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[--hc-border] bg-[--hc-surface]/50">
                <th className="text-left px-4 py-2.5 font-semibold text-[--hc-text-muted]">
                  <button onClick={() => toggleSort('name')} className="flex items-center gap-1 hover:text-[--hc-text]">
                    User <ArrowUpDown size={10} />
                  </button>
                </th>
                <th className="text-left px-4 py-2.5 font-semibold text-[--hc-text-muted]">Status</th>
                <th className="text-center px-4 py-2.5 font-semibold text-[--hc-text-muted]">Streak</th>
                <th className="text-center px-4 py-2.5 font-semibold text-[--hc-text-muted]">Best</th>
                <th className="text-center px-4 py-2.5 font-semibold text-[--hc-text-muted]">Completed</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[--hc-text-muted]">Last Active</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[--hc-text-muted]">
                  <button onClick={() => toggleSort('created_at')} className="flex items-center gap-1 hover:text-[--hc-text]">
                    Joined <ArrowUpDown size={10} />
                  </button>
                </th>
                <th className="text-center px-4 py-2.5 font-semibold text-[--hc-text-muted]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[--hc-border]">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-[--hc-surface]/30 transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[--hc-primary] flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[--hc-text] truncate flex items-center gap-1">
                          {user.name}
                          {user.is_admin && <Shield size={10} className="text-[--hc-accent]" />}
                        </p>
                        <p className="text-[--hc-text-muted] truncate">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      user.onboarding_complete
                        ? 'bg-[--hc-success-soft] text-[--hc-success]'
                        : 'bg-[--hc-warning-soft] text-[--hc-warning]'
                    }`}>
                      {user.onboarding_complete ? 'Active' : 'Pending'}
                    </span>
                    {user.sso_user && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-[--hc-primary]/10 text-[--hc-primary] ml-1">
                        SSO
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`font-bold tabular-nums ${user.current_streak > 0 ? 'text-[--hc-accent]' : 'text-[--hc-text-muted]'}`}>
                      {user.current_streak}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center font-medium text-[--hc-text] tabular-nums">
                    {user.best_streak}
                  </td>
                  <td className="px-4 py-2.5 text-center font-medium text-[--hc-text] tabular-nums">
                    {user.total_completed_days}
                  </td>
                  <td className="px-4 py-2.5 text-[--hc-text-muted]">
                    {user.last_active ? format(new Date(user.last_active + 'T12:00:00'), 'MMM d, yyyy') : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-[--hc-text-muted]">
                    {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={() => setSelectedUser(user.id)}
                      data-testid={`admin-view-user-${user.id}`}
                      className="p-1.5 rounded-lg hover:bg-[--hc-surface] text-[--hc-text-muted] hover:text-[--hc-primary] transition-colors"
                      title="View details"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-[--hc-text-muted]">
                    {search ? `No users found for "${search}"` : 'No users yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[--hc-border] flex items-center justify-between">
            <span className="text-xs text-[--hc-text-muted]">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg hover:bg-[--hc-surface] disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg hover:bg-[--hc-surface] disabled:opacity-30 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal userId={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
}
